/**
 * NMS Shop & Explore Bar Component
 * The Men / Women switch itself is pure CSS, so the bar works without this file.
 * This only remembers the shopper's last choice and broadcasts the change.
 */

const NMS_SHOP_EXPLORE_STORAGE_KEY = 'nms:shop-explore-gender';
const NMS_SHOP_EXPLORE_GENDERS = ['men', 'women'];

class NMSShopExploreBar extends window.NMSBaseClass {
  constructor(element) {
    super();

    // REQUIRED: Store element reference for scoped operations
    this.element = element;
    this.sectionId = element.dataset.sectionId || `section-${Date.now()}`;

    // REQUIRED: Register instance in global namespace for external access
    window.neemans = window.neemans || {};
    window.neemans.shopExploreBar = window.neemans.shopExploreBar || {};
    window.neemans.shopExploreBar[this.sectionId] = this;
  }

  init() {
    // REQUIRED: Override base init to scope operations to this.element
    this.#setupComponentRefs();
    this.#applyStoredPreference();
    this.#bindComponentEvents();
  }

  destroy() {
    super.destroy();

    if (window.neemans.shopExploreBar?.[this.sectionId]) {
      delete window.neemans.shopExploreBar[this.sectionId];
    }
  }

  // REQUIRED: Component-scoped ref setup (override base class)
  #setupComponentRefs() {
    this.refs = this.refs || {};
    this.refs.genderInputs = Array.from(this.element.querySelectorAll('[ref="genderInputs[]"]'));
  }

  #bindComponentEvents() {
    for (const input of this.refs.genderInputs) {
      input.addEventListener('change', () => {
        if (!input.checked) return;

        this.#storeGender(input.value);

        this.dispatchNMSEvent('shop-explore-gender-changed', {
          gender: input.value,
          sectionId: this.sectionId,
        });
      });
    }
  }

  // A returning shopper keeps the tab they picked last, whatever the section default is
  #applyStoredPreference() {
    // Tabs hidden: there is no way to switch back, so the section default always wins
    if (this.element.hasAttribute('data-lock-gender')) return;

    const stored = this.#readStoredGender();
    if (!stored || stored === this.getCurrentGender()) return;

    const input = this.refs.genderInputs.find(candidate => candidate.value === stored);
    if (!input) return;

    input.checked = true;
  }

  #readStoredGender() {
    try {
      const value = window.localStorage.getItem(NMS_SHOP_EXPLORE_STORAGE_KEY);
      return NMS_SHOP_EXPLORE_GENDERS.includes(value) ? value : null;
    } catch (error) {
      // Private browsing or blocked storage - the CSS default stands
      return null;
    }
  }

  #storeGender(gender) {
    try {
      window.localStorage.setItem(NMS_SHOP_EXPLORE_STORAGE_KEY, gender);
    } catch (error) {
      // Nothing to do - the choice still applies for this page view
    }
  }

  // Public API for external access
  getCurrentGender() {
    const checked = this.refs.genderInputs.find(input => input.checked);
    return checked ? checked.value : this.element.dataset.defaultGender || 'men';
  }

  switchToGender(gender) {
    if (!NMS_SHOP_EXPLORE_GENDERS.includes(gender)) return;

    const input = this.refs.genderInputs.find(candidate => candidate.value === gender);
    if (!input || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

// REQUIRED: Standard component initialization pattern
function initNMSShopExploreBar() {
  if (!window.NMSBaseClass) {
    console.error('NMS Shop & Explore Bar: NMSBaseClass not found. Make sure nms-base-class.js loads first.');
    return;
  }

  const elements = document.querySelectorAll('nms-shop-explore-bar');

  for (const element of elements) {
    if (element.hasAttribute('data-initialized')) continue;

    try {
      const instance = new NMSShopExploreBar(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    } catch (error) {
      console.error('Failed to initialize NMS Shop & Explore Bar:', error, element);
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSShopExploreBar);
} else {
  initNMSShopExploreBar();
}

// Re-initialize when new content is added (theme editor, AJAX, etc.)
document.addEventListener('nms:content-updated', initNMSShopExploreBar);
document.addEventListener('shopify:section:load', initNMSShopExploreBar);

// Export for debugging and manual initialization
window.initNMSShopExploreBar = initNMSShopExploreBar;

// Global API for accessing instances
window.getNMSShopExploreBar = function (sectionId) {
  if (sectionId) {
    return window.neemans?.shopExploreBar?.[sectionId] || null;
  }
  return window.neemans?.shopExploreBar || {};
};
